import type { NotificaApi, SeveritaNotificaApi } from "~~/shared/data-model/notifica_api";

// Funzioni pure per mostrare le notifiche: come si descrive una notifica (chiavi i18n e icona), quando è successo («3 min fa») e il raggruppamento per giorno.

export interface DescrizioneNotifica {
  icona: string;
  chiaveTitolo: string;
  /** Riga di testo sotto il titolo; assente se il titolo basta. */
  chiaveTesto: string | null;
  parametri: Record<string, string | number>;
}

const testo = (v: unknown): string => (typeof v === "string" || typeof v === "number" ? String(v) : "");

/** Chiavi i18n e parametri per titolo e testo di una notifica, dal tipo e dai parametri salvati. */
export function descriviNotifica(n: Pick<NotificaApi, "tipo" | "parametri">): DescrizioneNotifica {
  const p = n.parametri ?? {};
  if (n.tipo === "allarme") {
    const dispositivo = testo(p.nome_dispositivo) || testo(p.identificativo) || "—";
    return {
      icona: "fal fa-triangle-exclamation",
      chiaveTitolo: "centro_notifiche.tipi.allarme.titolo",
      chiaveTesto: p.bloccante === true ? "centro_notifiche.tipi.allarme.testo_bloccante" : "centro_notifiche.tipi.allarme.testo",
      parametri: { dispositivo, codice: testo(p.codice) || "—" },
    };
  }
  if (n.tipo === "allerta_meteo") return { icona: "fal fa-cloud-bolt", chiaveTitolo: "centro_notifiche.tipi.allerta_meteo.titolo", chiaveTesto: null, parametri: {} };
  return { icona: "fal fa-circle-info", chiaveTitolo: "centro_notifiche.tipi.sistema.titolo", chiaveTesto: null, parametri: {} };
}

/** Colore della barra di gravità (solo palette: le gravità alte in terracotta; la gravità si legge anche dall'etichetta). */
export const CLASSE_BARRA_GRAVITA: Record<SeveritaNotificaApi, string> = {
  info: "bg-border-strong",
  warn: "bg-text-accent/60",
  error: "bg-accent",
  critical: "bg-accent",
};

const MINUTO = 60;
const ORA = 3600;
const GIORNO = 86400;

/** «ora», «3 minuti fa», «2 ore fa», «ieri», «3 giorni fa»; oltre la settimana la data breve. */
export function tempoRelativo(iso: string, adesso: Date, lingua: string): string {
  const quando = new Date(iso);
  if (Number.isNaN(quando.getTime())) return "";
  const secondi = Math.max(0, Math.round((adesso.getTime() - quando.getTime()) / 1000));
  const rtf = new Intl.RelativeTimeFormat(lingua, { numeric: "auto" });
  if (secondi < MINUTO) return rtf.format(0, "second");
  if (secondi < ORA) return rtf.format(-Math.floor(secondi / MINUTO), "minute");
  if (secondi < GIORNO) return rtf.format(-Math.floor(secondi / ORA), "hour");
  if (secondi < 7 * GIORNO) return rtf.format(-Math.floor(secondi / GIORNO), "day");
  return new Intl.DateTimeFormat(lingua, { dateStyle: "short" }).format(quando);
}

export interface GruppoGiorno<T> {
  /** Giorno locale `AAAA-MM-GG`. */
  chiave: string;
  etichetta: string;
  righe: T[];
}

const chiaveGiorno = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** Raggruppa per giorno locale (dal più recente, mantenendo l'ordine delle righe); «Oggi» e «Ieri» con le etichette date, gli altri con la data estesa. */
export function raggruppaPerGiorno<T extends { createdAt: string }>(righe: readonly T[], adesso: Date, lingua: string, etichette: { oggi: string; ieri: string }): GruppoGiorno<T>[] {
  const oggi = chiaveGiorno(adesso);
  const ieriData = new Date(adesso);
  ieriData.setDate(ieriData.getDate() - 1);
  const ieri = chiaveGiorno(ieriData);
  const formato = new Intl.DateTimeFormat(lingua, { dateStyle: "long" });
  const gruppi: GruppoGiorno<T>[] = [];
  for (const riga of righe) {
    const data = new Date(riga.createdAt);
    if (Number.isNaN(data.getTime())) continue;
    const chiave = chiaveGiorno(data);
    let gruppo = gruppi.find((g) => g.chiave === chiave);
    if (!gruppo) {
      gruppo = { chiave, etichetta: chiave === oggi ? etichette.oggi : chiave === ieri ? etichette.ieri : formato.format(data), righe: [] };
      gruppi.push(gruppo);
    }
    gruppo.righe.push(riga);
  }
  return gruppi;
}
